/**
 * @fileoverview WebSocket client for Adivina Adivinador (Riddle - Tile 10) Admin Console.
 * Maintains real-time bidirectional communication with the server for state updates,
 * projection commands, layout modifications, and telemetry.
 * 
 * @module utilities/WS/Admin/RiddleWS
 */

let riddleAdminWebSocket = null;
let isRiddleAdminConnected = false;
let reconnectionTimeoutIdentifier = null;
let connectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 15;
const RECONNECTION_DELAY_MILLISECONDS = 3000;

/**
 * Establishes a persistent WebSocket connection for the Riddle admin console.
 * 
 * @function ConnectWebSocketForRiddleAdmin
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked on message received.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked on status change ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 */
export const ConnectWebSocketForRiddleAdmin = (
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    if (riddleAdminWebSocket || isRiddleAdminConnected) {
        return;
    }

    const establishConnection = () => {
        if (connectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        connectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const webSocketEndpointUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=superadmin`;
            riddleAdminWebSocket = new WebSocket(webSocketEndpointUrl);

            riddleAdminWebSocket.onopen = () => {
                isRiddleAdminConnected = true;
                connectionAttemptCounter = 0;
                onStatusChange('connected');

                // Request initial snapshot upon successful connection
                RequestRiddleState();
            };

            riddleAdminWebSocket.onclose = () => {
                isRiddleAdminConnected = false;
                riddleAdminWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            riddleAdminWebSocket.onerror = () => {
                onStatusChange('error');
            };

            riddleAdminWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Non-JSON packets are ignored
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Riddle admin WebSocket connection.
 * 
 * @function SendRiddleAdminMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendRiddleAdminMessage = (payloadObject) => {
    try {
        if (riddleAdminWebSocket && riddleAdminWebSocket.readyState === WebSocket.OPEN) {
            riddleAdminWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (sendError) {
        return false;
    }
};

/**
 * Closes and cleans up the Riddle admin WebSocket connection.
 * 
 * @function DisconnectWebSocketForRiddleAdmin
 */
export const DisconnectWebSocketForRiddleAdmin = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (riddleAdminWebSocket) {
            riddleAdminWebSocket.onopen = null;
            riddleAdminWebSocket.onclose = null;
            riddleAdminWebSocket.onerror = null;
            riddleAdminWebSocket.onmessage = null;

            if (riddleAdminWebSocket.readyState === WebSocket.OPEN) {
                riddleAdminWebSocket.close();
            } else if (riddleAdminWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = riddleAdminWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Ignore
                    }
                };
            }

            riddleAdminWebSocket = null;
            isRiddleAdminConnected = false;
            connectionAttemptCounter = 0;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};

// =============================================================================
// TYPED ACTION HELPERS FOR RIDDLE OPERATOR
// =============================================================================

/**
 * Requests the current synchronized game state snapshot.
 * 
 * @function RequestRiddleState
 * @returns {boolean}
 */
export const RequestRiddleState = () => {
    return SendRiddleAdminMessage({
        type: 'riddle_get_state'
    });
};

/**
 * Emits command to project cover screen.
 * 
 * @function SendRiddleShowCover
 * @returns {boolean}
 */
export const SendRiddleShowCover = () => {
    return SendRiddleAdminMessage({
        type: 'riddle_show_cover'
    });
};

/**
 * Emits command to project game arena screen.
 * 
 * @function SendRiddleShowGame
 * @returns {boolean}
 */
export const SendRiddleShowGame = () => {
    return SendRiddleAdminMessage({
        type: 'riddle_show_game'
    });
};

/**
 * Emits active question to screen and fans.
 * 
 * @function SendRiddleQuestion
 * @param {Object} questionData - Question payload.
 * @returns {boolean}
 */
export const SendRiddleQuestion = (questionData) => {
    return SendRiddleAdminMessage({
        type: 'riddle_send_question',
        data: questionData
    });
};

/**
 * Emits display item command.
 * 
 * @function SendRiddleDisplayItem
 * @param {Object|null} [questionData=null] - Question payload.
 * @returns {boolean}
 */
export const SendRiddleDisplayItem = (questionData = null) => {
    return SendRiddleAdminMessage({
        type: 'riddle_display_item',
        data: questionData
    });
};

/**
 * Emits screen layout change command.
 * 
 * @function SendRiddleChangeLayout
 * @param {number} layout - Screen layout identifier (1..4).
 * @returns {boolean}
 */
export const SendRiddleChangeLayout = (layout) => {
    return SendRiddleAdminMessage({
        type: 'riddle_change_layout',
        screenLayout: Number(layout),
        data: {
            screenLayout: Number(layout)
        }
    });
};

/**
 * Emits send results command.
 * 
 * @function SendRiddleResults
 * @returns {boolean}
 */
export const SendRiddleResults = () => {
    return SendRiddleAdminMessage({
        type: 'riddle_send_results'
    });
};

/**
 * Emits reset results command.
 * 
 * @function SendRiddleResetResults
 * @returns {boolean}
 */
export const SendRiddleResetResults = () => {
    return SendRiddleAdminMessage({
        type: 'riddle_reset_results'
    });
};
